export const EMPTY_FORM = {
  Address: "",
  Postcode: "",
  Area: "",
  latitude: "",
  longitude: "",
};

const OFFICE_ID_KEYS = [
  "id",
  "pk",
  "office_id",
  "Office_id",
  "officeId",
  "ID",
  "Id",
];

export function pick(obj, keys, fallback = "") {
  if (!obj || typeof obj !== "object") return fallback;
  for (const key of keys) {
    if (obj[key] != null && obj[key] !== "") return obj[key];
  }
  return fallback;
}

export function unwrapOffice(item) {
  if (!item || typeof item !== "object") return item;
  let row = item;
  if (item.properties && typeof item.properties === "object") {
    row = { id: item.id ?? item.pk, ...item.properties, ...item };
  }
  if (row.office && typeof row.office === "object" && !Array.isArray(row.office)) {
    row = {
      ...row.office,
      ...row,
      id: row.id ?? row.office.id ?? row.office.pk,
    };
  }
  if (
    row.data &&
    typeof row.data === "object" &&
    !Array.isArray(row.data) &&
    (row.data.id != null ||
      row.data.pk != null ||
      row.data.Address != null ||
      row.data.address != null)
  ) {
    row = { ...row, ...row.data };
  }
  return row;
}

export function officeId(office) {
  const row = unwrapOffice(office);
  let id = pick(row, OFFICE_ID_KEYS);
  if (id && typeof id === "object") {
    id = pick(id, OFFICE_ID_KEYS);
  }
  if (id == null || id === "") {
    const url = pick(row, ["url", "Url"]);
    const match = String(url || "").match(/(\d+)\/?$/);
    if (match) id = match[1];
  }
  if (id == null || id === "" || typeof id === "object") return null;
  return id;
}

export function parsePosition(value) {
  if (value == null || value === "") {
    return { latitude: "", longitude: "" };
  }
  if (typeof value === "object") {
    const latitude = pick(value, ["latitude", "lat", "Latitude"]);
    const longitude = pick(value, ["longitude", "lng", "lon", "Longitude"]);
    return {
      latitude: latitude === "" ? "" : String(latitude),
      longitude: longitude === "" ? "" : String(longitude),
    };
  }
  const text = String(value).trim();
  const point = text.match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i);
  if (point) {
    return { longitude: point[1], latitude: point[2] };
  }
  const parts = text.split(",").map((part) => part.trim());
  if (parts.length >= 2) {
    return { latitude: parts[0], longitude: parts[1] };
  }
  return { latitude: "", longitude: "" };
}

export function formatPosition(office) {
  const parsed = parsePosition(
    office?.position ?? office?.Position ?? office?.geoposition
  );
  if (parsed.latitude && parsed.longitude) {
    return `${parsed.latitude}, ${parsed.longitude}`;
  }
  return "—";
}

export function toForm(office) {
  const parsed = parsePosition(
    office?.position ?? office?.Position ?? office?.geoposition
  );
  return {
    Address: String(pick(office, ["Address", "address"], "")),
    Postcode: String(pick(office, ["Postcode", "postcode", "postal_code"], "")),
    Area: String(pick(office, ["Area", "area"], "")),
    latitude: parsed.latitude,
    longitude: parsed.longitude,
  };
}

export function buildPosition(latitude, longitude) {
  return `${String(latitude).trim()},${String(longitude).trim()}`;
}

export function isValidCoord(value, min, max) {
  const n = Number(value);
  return Number.isFinite(n) && n >= min && n <= max;
}

export function matchesSearch(office, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    pick(office, ["Address", "address"]),
    pick(office, ["Postcode", "postcode"]),
    pick(office, ["Area", "area"]),
    formatPosition(office),
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function authHeaders(token, json = false) {
  const headers = { Authorization: `Token ${token}` };
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

export async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function asOfficeList(value) {
  if (!Array.isArray(value)) return null;
  return value.map((item) => unwrapOffice(item));
}

function officesFromObject(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  const entries = Object.entries(obj).filter(
    ([key]) => !["message", "status", "success"].includes(key)
  );
  if (!entries.length) return [];
  const nested = entries.filter(
    ([, value]) => value && typeof value === "object" && !Array.isArray(value)
  );
  if (nested.length && nested.length === entries.length) {
    return nested.map(([key, value]) => {
      const row = unwrapOffice(value);
      return { ...row, id: officeId(row) ?? key };
    });
  }
  if (
    officeId(obj) != null ||
    obj.Address != null ||
    obj.address != null ||
    obj.Postcode != null
  ) {
    return [unwrapOffice(obj)];
  }
  return [];
}

export function listFromPayload(payload) {
  const lists = [
    asOfficeList(payload),
    asOfficeList(payload?.data),
    asOfficeList(payload?.results),
    asOfficeList(payload?.offices),
    asOfficeList(payload?.data?.results),
    asOfficeList(payload?.data?.offices),
    asOfficeList(payload?.data?.data),
  ];
  for (const list of lists) {
    if (list && list.length) return list;
  }
  const fromObject = officesFromObject(payload?.data);
  if (fromObject.length) return fromObject;
  return [];
}

export function isApiSuccess(response, json) {
  const type = json?.message?.type;
  if (type === "error") return false;
  if (type === "success") return true;
  return Boolean(response?.ok);
}

function asText(value) {
  if (value == null || value === false) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return asText(value[0]);
  if (typeof value === "object") {
    return asText(value.description || value.detail || value.message);
  }
  return String(value);
}

export function errorText(json, fallback) {
  if (json?.message?.type === "success") return fallback;
  return (
    asText(json?.message?.description) ||
    asText(json?.detail) ||
    (typeof json?.error === "string" ? json.error : "") ||
    (typeof json?.message === "string" ? json.message : "") ||
    fallback
  );
}

export function fieldErrorsFromApi(json) {
  if (!json || typeof json !== "object") return {};
  const next = {};
  const map = {
    Address: "Address",
    address: "Address",
    Postcode: "Postcode",
    postcode: "Postcode",
    Area: "Area",
    area: "Area",
    position: "latitude",
    latitude: "latitude",
    longitude: "longitude",
  };
  Object.keys(map).forEach((key) => {
    const value = json[key];
    if (!value) return;
    const text = Array.isArray(value) ? value[0] : String(value);
    if (text) next[map[key]] = text;
  });
  return next;
}

export function officePayload(form) {
  return {
    Address: String(form.Address).trim(),
    Postcode: String(form.Postcode).trim(),
    Area: String(form.Area).trim(),
    position: buildPosition(form.latitude, form.longitude),
  };
}
